import styled from '@emotion/styled';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { wait } from '../../lib/wait';
import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { useI18n } from '../../hooks/useI18nFormatters';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';

import { CloseButton } from '../../components/buttons/CloseButton';
import { SolidButton } from '../../components/buttons/SolidButton';
import { Heading3Medium } from '../../components/common/text/Heading3Medium';
import { TextField } from '../../components/forms/TextField';
import { Dialog } from '../../components/overlay/Dialog';

import { scriptsStore } from './ScriptsStore';

export function ScriptDialog() {
  const { i18n } = useI18n();
  const formId = 'script-dialog-form';
  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
    },
  });

  function onSubmit(data) {
    setIsSaving(true);
    const values = { ...data };

    const waitPromise = wait(500);
    const setPromise = scriptsStore.createScript({
      script: { name: values.name, code: '' },
    });

    Promise.all([waitPromise, setPromise])
      .then(([, result]) => {
        ToastManager.add({
          icon: glyph.core.paper,
          i18n: {
            key: 'script.createdMessage',
            props: { name: result.name },
          },
        });

        setIsSaving(false);
        handleClose();

        if (result != null) {
          RouteManager.toScript(encodeURIComponent(result.id));
        }
      })
      .catch((error) => {
        ToastManager.handleError(error);
        setIsSaving(false);
      });
  }

  function handleClose() {
    RouteManager.pushState({});
  }

  return (
    <Dialog onClose={handleClose} isOpen>
      <ScriptDialog.Root>
        <ScriptDialog.Header>
          <Heading3Medium>{i18n.messageFormatter(`script.newScript`)}</Heading3Medium>

          <CloseButton onPress={handleClose} />
        </ScriptDialog.Header>

        <ScriptDialog.Form id={formId} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            autoFocus
            autoComplete="off"
            orientation="vertical"
            type="text"
            name="name"
            label={i18n.messageFormatter('common.name')}
            placeholder={i18n.messageFormatter('script.newScriptName')}
            defaultValue=""
            register={register}
            required
            minLength={1}
            error={errors.name}
            onKeyDown={(event) => {
              // TextField stops every key, so let Escape through to close the dialog.
              if (event.key === 'Escape') {
                event.continuePropagation();
              }
            }}
          />
        </ScriptDialog.Form>

        <ScriptDialog.Footer>
          <SolidButton
            form={formId}
            type="submit"
            styleFlat={true}
            styleColor="blue"
            styleWidth="full"
            isDisabled={isSaving === true}
          >
            <SolidButton.Text>{i18n.messageFormatter('common.create')}</SolidButton.Text>
          </SolidButton>
        </ScriptDialog.Footer>
      </ScriptDialog.Root>
    </Dialog>
  );
}

ScriptDialog.Root = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 320px;
  max-width: 320px;
`;

ScriptDialog.Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${su(2, 2, 0, 2)};
`;

ScriptDialog.Form = styled.form`
  padding: ${su(0, 2, 1, 2)};

  ${TextField.Root} {
    margin: ${su(2)} 0;
  }
`;

ScriptDialog.Footer = styled.footer`
  padding: ${su(0, 1, 1, 1)};
`;
